/**
 * Spanish sounds, from Spanish spelling (TASKS T61, RESEARCH §5).
 *
 * Spanish orthography is very nearly phonemic, which is what makes this tractable: a small set of
 * rules turns written Spanish into the sounds a speaker actually makes, with no dictionary. That is
 * the whole reason speech feedback in this game can be *segmental and specific* — "the rr is a
 * trill, not a single tap" — rather than a score.
 *
 * ## Why segmental, and only segmental
 *
 * The ASR pronunciation-training evidence (Ngo, Chen & Lai 2024) finds a large effect on **segmental**
 * features and a small one on suprasegmental, and finds explicit corrective feedback beating plain
 * dictation. So this module names individual sounds and says nothing whatsoever about rhythm,
 * intonation, or fluency — the app is not permitted to claim it can hear those, and a module that
 * could not compute them cannot later be asked to display them.
 *
 * ## Mexican Spanish, not Peninsular
 *
 * Three choices here are variety decisions, not simplifications:
 *
 *  - **seseo** — `c` before `e`/`i`, and `z`, are `/s/`. Never the Peninsular `/θ/`.
 *  - **yeísmo** — `ll` and consonantal `y` are the same sound.
 *  - **`b` and `v` are one sound.** Not a shortcut: they are genuinely identical in Spanish, and
 *    telling the learner so is one of the more useful things this feedback does.
 *
 * Pure. No DOM, no clock, no I/O.
 */

import { stripAccents } from './accents';

/**
 * A sound, with the letters that spelled it.
 *
 * `symbol` is an internal id and is never shown — the learner is learning Spanish, not IPA. What he
 * sees is `letters`, plus whatever `CONTRAST_NOTES` has to say about the pair.
 */
export interface Phoneme {
  symbol: string;
  /** As written, lower-cased. `rr`, `ch`, `qu`, `gu` stay together. */
  letters: string;
}

/** How the learner is told about a sound when nothing more specific applies. */
export const SOUND_NAMES: Readonly<Record<string, string>> = {
  a: 'the pure a',
  e: 'the pure e',
  i: 'the pure i',
  o: 'the pure o',
  u: 'the pure u',
  p: 'p',
  b: 'the b/v sound',
  t: 't',
  d: 'd',
  k: 'the k sound',
  g: 'the hard g',
  f: 'f',
  s: 'the s sound',
  x: 'the j sound, from the throat',
  ch: 'ch',
  m: 'm',
  n: 'n',
  ny: 'ñ',
  l: 'l',
  y: 'the ll/y sound',
  rr: 'the rolled rr',
  r: 'the single tapped r',
  w: 'the w glide',
};

const VOWELS = new Set(['a', 'e', 'i', 'o', 'u']);

/**
 * Turn written Spanish into sounds.
 *
 * Accents are stripped first — they mark stress, not a different vowel, and Spanish has exactly five
 * vowels whether or not one carries an acute. `ü` is kept, because it is the one diacritic that does
 * change a sound (`güe` is `gwe`, `gue` is `ge`).
 */
export function phonemesOf(word: string): Phoneme[] {
  const text = normaliseForSound(word);
  const out: Phoneme[] = [];
  let i = 0;

  const previous = (): string | undefined => out[out.length - 1]?.symbol;

  while (i < text.length) {
    const ch = text[i]!;
    const next = text[i + 1];
    const pair = ch + (next ?? '');

    // Digraphs first: two letters that are one sound.
    if (pair === 'ch') {
      out.push({ symbol: 'ch', letters: 'ch' });
      i += 2;
      continue;
    }
    if (pair === 'll') {
      out.push({ symbol: 'y', letters: 'll' });
      i += 2;
      continue;
    }
    if (pair === 'rr') {
      out.push({ symbol: 'rr', letters: 'rr' });
      i += 2;
      continue;
    }
    if (pair === 'qu' && (text[i + 2] === 'e' || text[i + 2] === 'i')) {
      out.push({ symbol: 'k', letters: 'qu' });
      i += 2;
      continue;
    }
    if (pair === 'gu' && (text[i + 2] === 'e' || text[i + 2] === 'i')) {
      // The `u` is silent: `guerra` is `gerra`. This is the rule `güe` exists to override.
      out.push({ symbol: 'g', letters: 'gu' });
      i += 2;
      continue;
    }
    if (ch === 'g' && next === 'ü') {
      out.push({ symbol: 'g', letters: 'gü' });
      out.push({ symbol: 'w', letters: 'ü' });
      i += 2;
      continue;
    }

    switch (ch) {
      case 'a':
      case 'e':
      case 'i':
      case 'o':
      case 'u':
        out.push({ symbol: ch, letters: ch });
        break;
      case 'ü':
        out.push({ symbol: 'u', letters: 'ü' });
        break;
      case 'c':
        // seseo: `ce`/`ci` are `/s/`, never the Peninsular `/θ/`.
        out.push({ symbol: next === 'e' || next === 'i' ? 's' : 'k', letters: 'c' });
        break;
      case 'z':
        out.push({ symbol: 's', letters: 'z' });
        break;
      case 'q':
        out.push({ symbol: 'k', letters: 'q' });
        break;
      case 'g':
        out.push({ symbol: next === 'e' || next === 'i' ? 'x' : 'g', letters: 'g' });
        break;
      case 'j':
        out.push({ symbol: 'x', letters: 'j' });
        break;
      case 'h':
        // Silent, always. Nothing is pushed, which is itself a fact worth feeding back.
        break;
      case 'v':
        out.push({ symbol: 'b', letters: 'v' });
        break;
      case 'b':
        out.push({ symbol: 'b', letters: 'b' });
        break;
      case 'w':
        out.push({ symbol: 'w', letters: 'w' });
        break;
      case 'y':
        // Consonantal before a vowel (`yo`); the vowel `i` alone or finally (`hay`, `y`).
        out.push(
          next !== undefined && VOWELS.has(next)
            ? { symbol: 'y', letters: 'y' }
            : { symbol: 'i', letters: 'y' },
        );
        break;
      case 'ñ':
        out.push({ symbol: 'ny', letters: 'ñ' });
        break;
      case 'x':
        // `/ks/` as a rule. México and its family are the famous exceptions and are spelled with `x`
        // for historical reasons; they are handled by `SOUND_EXCEPTIONS` rather than by guessing.
        out.push({ symbol: 'k', letters: 'x' });
        out.push({ symbol: 's', letters: 'x' });
        break;
      case 'r': {
        // Word-initial, or after n / l / s, `r` is the **trill**: `rosa` and `rr` are one sound.
        const before = previous();
        const trill = before === undefined || before === 'n' || before === 'l' || before === 's';
        out.push({ symbol: trill ? 'rr' : 'r', letters: 'r' });
        break;
      }
      default:
        if (/[a-zñ]/.test(ch)) out.push({ symbol: ch, letters: ch });
        break;
    }
    i += 1;
  }

  return out;
}

/**
 * Words whose spelling lies about their sound.
 *
 * A short, explicit list rather than a rule: `x` is `/x/` in exactly the Mexican place names and
 * their derivatives, and inventing a rule for that would misfire on `examen` and `taxi`.
 */
const SOUND_EXCEPTIONS: Readonly<Record<string, string>> = {
  mexico: 'mejico',
  mexicano: 'mejicano',
  mexicana: 'mejicana',
  oaxaca: 'oajaca',
  texas: 'tejas',
};

/**
 * Lower-cased, stress accents removed, `ü` preserved.
 *
 * `stripAccents` folds `ü` to `u` along with the acutes, which is right for *scoring* and wrong here:
 * the acutes mark stress and change no sound, while the diaeresis is the one diacritic that does —
 * `pingüino` has a `w` in it and `guerra` does not. So it is carried across the strip on a private
 * placeholder rather than being handed to a function whose job is to remove diacritics.
 */
const DIAERESIS_MARK = '';

function normaliseForSound(word: string): string {
  const lowered = stripAccents(word.replace(/ü/gi, DIAERESIS_MARK))
    .toLowerCase()
    .replaceAll(DIAERESIS_MARK, 'ü')
    .trim();
  return SOUND_EXCEPTIONS[lowered] ?? lowered;
}

/** The sounds of a whole phrase, word by word. Word boundaries are dropped: this is one utterance. */
export function phonemesOfPhrase(phrase: string): Phoneme[] {
  return phrase
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .flatMap((word) => phonemesOf(word));
}

// ---------------------------------------------------------------------------------------------
// Comparing two utterances
// ---------------------------------------------------------------------------------------------

export type DiffKind = 'same' | 'substituted' | 'missing' | 'added';

export interface PhonemeDiff {
  kind: DiffKind;
  /** The sound that should have been there. Absent on `added`. */
  expected?: Phoneme;
  /** The sound that was there instead. Absent on `missing`. */
  heard?: Phoneme;
}

/**
 * Align two sound sequences and report where they differ.
 *
 * Needleman–Wunsch with unit costs — the standard edit alignment, chosen over a plain Levenshtein
 * distance because a *distance* is a score and this module is forbidden to produce one. What comes
 * out is a list of specific differences, which is what explicit corrective feedback needs.
 */
export function diffPhonemes(
  expected: readonly Phoneme[],
  heard: readonly Phoneme[],
): PhonemeDiff[] {
  const rows = expected.length;
  const cols = heard.length;
  const cost: number[][] = Array.from({ length: rows + 1 }, () => new Array<number>(cols + 1).fill(0));

  for (let i = 0; i <= rows; i += 1) cost[i]![0] = i;
  for (let j = 0; j <= cols; j += 1) cost[0]![j] = j;

  for (let i = 1; i <= rows; i += 1) {
    for (let j = 1; j <= cols; j += 1) {
      const same = expected[i - 1]!.symbol === heard[j - 1]!.symbol;
      cost[i]![j] = Math.min(
        cost[i - 1]![j - 1]! + (same ? 0 : 1),
        cost[i - 1]![j]! + 1,
        cost[i]![j - 1]! + 1,
      );
    }
  }

  const out: PhonemeDiff[] = [];
  let i = rows;
  let j = cols;
  while (i > 0 || j > 0) {
    const here = cost[i]![j]!;
    if (i > 0 && j > 0) {
      const same = expected[i - 1]!.symbol === heard[j - 1]!.symbol;
      if (here === cost[i - 1]![j - 1]! + (same ? 0 : 1)) {
        out.push({
          kind: same ? 'same' : 'substituted',
          expected: expected[i - 1]!,
          heard: heard[j - 1]!,
        });
        i -= 1;
        j -= 1;
        continue;
      }
    }
    if (i > 0 && here === cost[i - 1]![j]! + 1) {
      out.push({ kind: 'missing', expected: expected[i - 1]! });
      i -= 1;
      continue;
    }
    out.push({ kind: 'added', heard: heard[j - 1]! });
    j -= 1;
  }

  return out.reverse();
}

// ---------------------------------------------------------------------------------------------
// What to say about a difference
// ---------------------------------------------------------------------------------------------

/**
 * The specific contrasts worth naming, keyed `expected>heard`.
 *
 * Every one of these is a mistake an English speaker actually makes, and every one names the *Spanish
 * fact* rather than the error. "b and v are the same sound in Spanish" is something he can use
 * tomorrow; "you said v instead of b" is not, because he thinks he did say b.
 */
export const CONTRAST_NOTES: Readonly<Record<string, string>> = {
  'rr>r': 'rr is a rolled r, held. A single tap is the r in "pero".',
  'r>rr': 'This r is a single tap, not a roll — the r in "pero", not in "perro".',
  'ny>n': 'ñ is n with a y in it: "niño", not "nino".',
  'n>ny': 'Plain n here, not ñ.',
  'x>k': 'j is made in the throat, further back than an English h.',
  'x>g': 'j is made in the throat, further back than an English h.',
  's>ch': 'c before e and i is an s in Mexican Spanish.',
  'b>f': 'b and v are the same sound in Spanish, and it is a b.',
  'y>l': 'll is not two l sounds — it is the y in "yo".',
  'y>i': 'll and y are a consonant here, not a vowel.',
  'k>g': 'This one is a k, unvoiced.',
  'g>k': 'This one is a hard g, voiced.',
  'e>i': 'Spanish e is one pure sound. It never glides into an i the way English "ay" does.',
  'o>u': 'Spanish o is one pure sound. It never glides into a u the way English "oh" does.',
  'i>e': 'Spanish i is the ee in "see", short and pure.',
  'u>o': 'Spanish u is the oo in "food", short and pure.',
  'a>e': 'Spanish a is one open sound, the same every time.',
};

/** One thing to tell the learner about one sound. */
export interface SegmentalNote {
  /** The letters, as written, that this is about. */
  letters: string;
  message: string;
}

/**
 * Turn the differences into things worth saying.
 *
 * Deliberately capped, and deliberately not exhaustive: a list of nine corrections is a list nobody
 * reads, and the learner can only work on one or two sounds at a time. Ordered as they occur in the
 * word so the note can be followed while saying it again.
 */
export function segmentalNotes(diffs: readonly PhonemeDiff[], limit = 3): SegmentalNote[] {
  const notes: SegmentalNote[] = [];
  for (const diff of diffs) {
    if (diff.kind === 'same') continue;
    if (notes.length >= limit) break;

    if (diff.kind === 'substituted' && diff.expected && diff.heard) {
      const key = `${diff.expected.symbol}>${diff.heard.symbol}`;
      notes.push({
        letters: diff.expected.letters,
        message:
          CONTRAST_NOTES[key] ??
          `Here it is ${SOUND_NAMES[diff.expected.symbol] ?? diff.expected.letters}.`,
      });
      continue;
    }
    if (diff.kind === 'missing' && diff.expected) {
      notes.push({
        letters: diff.expected.letters,
        message: `${capitalise(SOUND_NAMES[diff.expected.symbol] ?? diff.expected.letters)} is not there.`,
      });
      continue;
    }
    if (diff.kind === 'added' && diff.heard) {
      notes.push({
        letters: diff.heard.letters,
        message: `There is an extra sound here — ${SOUND_NAMES[diff.heard.symbol] ?? diff.heard.letters}.`,
      });
    }
  }
  return notes;
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
